import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";

// ─── GraphQL ────────────────────────────────────────────────────────────────

const GET_ALL_TERMS = gql`
  query GetAllTerms($type: TermsType) {
    getAllTerms(type: $type) {
      _id
      type
      title
      contentText
      termsUrl
      version
      isActive
      createdAt
      updatedAt
    }
  }
`;

const GET_ACTIVE_TERMS = gql`
  query GetActiveTerms($type: TermsType!) {
    getActiveTerms(type: $type) {
      _id
      type
      title
      contentText
      termsUrl
      version
      createdAt
    }
  }
`;

const CREATE_TERMS = gql`
  mutation CreateTerms(
    $type: TermsType!
    $title: String
    $contentText: String
    $termsUrl: String
  ) {
    createTerms(
      type: $type
      title: $title
      contentText: $contentText
      termsUrl: $termsUrl
    ) {
      _id
      type
      title
      contentText
      termsUrl
      version
      isActive
      createdAt
    }
  }
`;

const UPDATE_TERMS = gql`
  mutation UpdateTerms(
    $id: ID!
    $title: String
    $contentText: String
    $isActive: Boolean
    $termsUrl: String
  ) {
    updateTerms(
      id: $id
      title: $title
      contentText: $contentText
      isActive: $isActive
      termsUrl: $termsUrl
    ) {
      _id
      type
      title
      contentText
      termsUrl
      version
      isActive
      updatedAt
    }
  }
`;

const DELETE_TERMS = gql`
  mutation DeleteTerms($id: ID!) {
    deleteTerms(id: $id)
  }
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: "COMMON",  label: "Common", icon: "🌐", color: "#2563EB", light: "#EFF6FF", badge: "All Users"  },
  { id: "SELLER",  label: "Seller", icon: "🛍️", color: "#7C3AED", light: "#F5F3FF", badge: "Vendors"    },
  { id: "BUYER",   label: "Buyer",  icon: "🛒", color: "#059669", light: "#ECFDF5", badge: "Customers"  },
];

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "—";

// ─── Styles ──────────────────────────────────────────────────────────────────

const S = {
  wrap: {
    minHeight: "100vh",
    background: "#F8FAFC",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    padding: "0 0 60px",
  },
  container: { maxWidth: 900, margin: "0 auto", padding: "0 20px" },

  // Header
  header: {
    background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
    padding: "32px 20px 28px",
    marginBottom: 32,
  },
  headerInner: { maxWidth: 900, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" },
  h1: { margin: 0, fontSize: 26, fontWeight: 700, color: "#F8FAFC", letterSpacing: "-0.5px" },
  subtitle: { margin: "4px 0 0", fontSize: 14, color: "#94A3B8" },
  refreshBtn: {
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "#CBD5E1",
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 13,
    transition: "all .2s",
  },

  // Tabs
  tabs: { display: "flex", gap: 10, marginBottom: 24 },
  tab: (active, tab) => ({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    padding: "12px 8px",
    borderRadius: 12,
    border: `2px solid ${active ? tab.color : "#E2E8F0"}`,
    background: active ? `linear-gradient(145deg, ${tab.color}, ${tab.color}cc)` : "#fff",
    color: active ? "#fff" : "#1E293B",
    cursor: "pointer",
    transition: "all .25s",
    fontSize: 13,
    fontWeight: 600,
  }),
  tabBadge: (active, tab) => ({
    fontSize: 10,
    padding: "2px 8px",
    borderRadius: 20,
    background: active ? "rgba(255,255,255,0.2)" : tab.light,
    color: active ? "#fff" : tab.color,
    fontWeight: 600,
  }),

  // Active terms banner
  banner: (tab) => ({
    background: `linear-gradient(145deg, ${tab.light}, #fff)`,
    border: `1px solid ${tab.color}33`,
    borderLeft: `4px solid ${tab.color}`,
    borderRadius: 12,
    padding: "16px 20px",
    marginBottom: 24,
    display: "flex",
    alignItems: "center",
    gap: 14,
  }),
  badgePill: (bg) => ({
    background: bg,
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    padding: "3px 8px",
    borderRadius: 20,
    letterSpacing: 1,
  }),

  // Card / form
  card: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #E2E8F0",
    boxShadow: "0 1px 4px rgba(0,0,0,.06)",
    padding: "24px",
    marginBottom: 24,
  },
  cardTitle: { margin: "0 0 20px", fontSize: 17, fontWeight: 700, color: "#0F172A", display: "flex", alignItems: "center", gap: 10 },
  iconCircle: (bg, color) => ({
    width: 36, height: 36, borderRadius: 10,
    background: bg, color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
  }),

  label: { display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6 },
  input: (color) => ({
    width: "100%", boxSizing: "border-box",
    padding: "10px 14px", borderRadius: 10,
    border: `1.5px solid ${color}55`,
    fontSize: 14, color: "#1E293B",
    outline: "none", transition: "border .2s",
    fontFamily: "inherit",
  }),

  // Submit
  submitBtn: (color) => ({
    background: `linear-gradient(145deg, ${color}, ${color}cc)`,
    color: "#fff", border: "none",
    padding: "12px 28px", borderRadius: 10,
    fontSize: 15, fontWeight: 700, cursor: "pointer",
    display: "flex", alignItems: "center", gap: 8,
    boxShadow: `0 4px 12px ${color}44`,
    transition: "transform .15s",
  }),
  cancelBtn: {
    background: "#F1F5F9", color: "#64748B", border: "none",
    padding: "12px 20px", borderRadius: 10,
    fontSize: 14, fontWeight: 600, cursor: "pointer",
  },

  // Search
  searchWrap: { display: "flex", gap: 12, alignItems: "center", marginBottom: 20 },
  searchBox: (color) => ({
    flex: 1, display: "flex", alignItems: "center", gap: 10,
    border: `1.5px solid ${color}44`, borderRadius: 10,
    padding: "9px 14px", background: "#fff",
  }),
  searchInput: {
    flex: 1, border: "none", outline: "none",
    fontSize: 14, color: "#1E293B", background: "transparent", fontFamily: "inherit",
  },
  statPill: (light, color) => ({
    background: light, color, fontWeight: 700,
    fontSize: 12, padding: "6px 14px", borderRadius: 20,
    whiteSpace: "nowrap",
  }),

  // Terms cards grid
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 },
  termCard: (active, tab) => ({
    borderRadius: 14,
    border: `1.5px solid ${active ? tab.color : "#E2E8F0"}`,
    background: active ? tab.light : "#fff",
    padding: "18px",
    transition: "box-shadow .2s",
    boxShadow: active ? `0 4px 16px ${tab.color}22` : "none",
  }),
  versionPill: (light, color) => ({
    background: light, color, fontSize: 11, fontWeight: 700,
    padding: "3px 9px", borderRadius: 20,
  }),
  activePill: (color) => ({
    background: color, color: "#fff", fontSize: 10, fontWeight: 700,
    padding: "3px 8px", borderRadius: 20, letterSpacing: 0.5,
  }),
  preview: { fontSize: 13, color: "#64748B", lineHeight: 1.6, margin: "10px 0 14px" },
  urlChip: (color) => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    background: color + "11", color, fontSize: 11, fontWeight: 600,
    padding: "3px 10px", borderRadius: 20, marginRight: 6, marginBottom: 4,
    textDecoration: "none",
  }),
  cardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 },
  actionBtn: (color, danger) => ({
    width: 32, height: 32, borderRadius: 8,
    border: `1px solid ${danger ? "#FECACA" : color + "33"}`,
    background: danger ? "#FEF2F2" : color + "0D",
    color: danger ? "#EF4444" : color,
    cursor: "pointer", fontSize: 14,
    display: "flex", alignItems: "center", justifyContent: "center",
  }),

  // Empty
  empty: { textAlign: "center", padding: "48px 24px", color: "#94A3B8" },
  emptyIcon: (light, color) => ({
    width: 64, height: 64, borderRadius: 20,
    background: light, color,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 28, margin: "0 auto 16px",
  }),

  // Notification
  notif: (type) => ({
    position: "fixed", top: 20, right: 20, zIndex: 1000,
    background: type === "success" ? "#064E3B" : "#7F1D1D",
    color: "#fff", padding: "12px 20px", borderRadius: 12,
    display: "flex", alignItems: "center", gap: 10,
    fontSize: 14, fontWeight: 600,
    boxShadow: "0 8px 24px rgba(0,0,0,.2)",
    animation: "slideIn .3s ease",
  }),

  divider: { border: "none", borderTop: "1px solid #E2E8F0", margin: "20px 0" },
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function TermsManager() {
  const [activeTab, setActiveTab] = useState("COMMON");

  // Form fields
  const [title, setTitle] = useState("");
  const [termsUrl, setTermsUrl] = useState(""); // single url string
  const [editId, setEditId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [notif, setNotif] = useState(null);

  const tab = TABS.find((t) => t.id === activeTab);

  // ── Queries / Mutations ─────────────────────────────────────────────────────
  const { loading, error, data, refetch } = useQuery(GET_ALL_TERMS, {
    variables: { type: activeTab },
    fetchPolicy: "network-only",
  });
  const { data: activeData } = useQuery(GET_ACTIVE_TERMS, {
    variables: { type: activeTab },
    fetchPolicy: "network-only",
  });
  const [createTerms] = useMutation(CREATE_TERMS);
  const [updateTerms] = useMutation(UPDATE_TERMS);
  const [deleteTerms] = useMutation(DELETE_TERMS);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const toast = (message, type = "success") => {
    setNotif({ message, type });
    setTimeout(() => setNotif(null), 3000);
  };

  const resetForm = () => {
    setTitle("");
    setTermsUrl("");
    setEditId(null);
  };

  const handleTabChange = (id) => {
    setActiveTab(id);
    resetForm();
    setSearchTerm("");
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: URL required
    const url = termsUrl.trim();
    if (!url) {
      toast("Please provide a valid URL!", "error");
      return;
    }

    // Backend expects a non‑null contentText – we send a placeholder.
    const finalContent = " "; // backend required field – not used in frontend when URL exists
    const finalUrl = url;

    try {
      if (editId) {
        await updateTerms({
          variables: {
            id: editId,
            title: title.trim() || undefined,
            contentText: finalContent,
            isActive: true,
            termsUrl: finalUrl,
          },
        });
        toast(`${tab.label} terms updated`);
      } else {
        await createTerms({
          variables: {
            type: activeTab,
            title: title.trim() || `Terms & Conditions (${tab.label})`,
            contentText: finalContent,
            termsUrl: finalUrl,
          },
        });
        toast(`${tab.label} terms created`);
      }
      resetForm();
      refetch();
    } catch (err) {
      toast(err.message, "error");
    }
  };

  // ── Edit / Delete / Activate ─────────────────────────────────────────────────
  const handleEdit = (term) => {
    setEditId(term._id);
    setTitle(term.title || "");
    setTermsUrl(term.termsUrl || "");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this terms document?")) return;
    try {
      await deleteTerms({ variables: { id } });
      toast(`${tab.label} terms deleted`);
      refetch();
    } catch (err) {
      toast(err.message, "error");
    }
  };

  const handleSetActive = async (id) => {
    try {
      await updateTerms({ variables: { id, isActive: true } });
      toast(`${tab.label} terms activated`);
      refetch();
    } catch (err) {
      toast(err.message, "error");
    }
  };

  // ── Data ─────────────────────────────────────────────────────────────────────
  const terms = data?.getAllTerms || [];
  const activeTerms = activeData?.getActiveTerms;
  const filtered = terms.filter((t) => {
    const q = searchTerm.toLowerCase();
    return (
      t.title?.toLowerCase().includes(q) ||
      t.contentText?.toLowerCase().includes(q) ||
      t.version?.toString().includes(q)
    );
  });

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={S.wrap}>
      <style>{`
        @keyframes slideIn { from { transform: translateX(40px); opacity: 0; } to { transform: none; opacity: 1; } }
        button:focus { outline: none; }
      `}</style>

      {/* Notification */}
      {notif && (
        <div style={S.notif(notif.type)}>
          {notif.type === "success" ? "✅" : "❌"} {notif.message}
        </div>
      )}

      {/* ── Header ── */}
      <div style={S.header}>
        <div style={S.headerInner}>
          <div>
            <h1 style={S.h1}>Terms & Conditions</h1>
            <p style={S.subtitle}>Manage legal agreements for all user types</p>
          </div>
          <button style={S.refreshBtn} onClick={() => refetch()}>⟳ Refresh</button>
        </div>
      </div>

      <div style={S.container}>
        {/* ── Tabs ── */}
        <div style={S.tabs}>
          {TABS.map((t) => (
            <button
              key={t.id}
              style={S.tab(activeTab === t.id, t)}
              onClick={() => handleTabChange(t.id)}
            >
              <span style={{ fontSize: 20 }}>{t.icon}</span>
              <span>{t.label}</span>
              <span style={S.tabBadge(activeTab === t.id, t)}>{t.badge}</span>
            </button>
          ))}
        </div>

        {/* ── Active Terms Banner ── */}
        {activeTerms && (
          <div style={S.banner(tab)}>
            <div>
              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                <span style={S.badgePill(tab.color)}>ACTIVE</span>
                <span style={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>v{activeTerms.version}</span>
              </div>
              <div style={{ fontWeight: 700, color: "#0F172A", fontSize: 15 }}>
                {activeTerms.title || `Terms & Conditions (${tab.label})`}
              </div>
              <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 2 }}>
                Effective from {fmt(activeTerms.createdAt)}
              </div>
            </div>
          </div>
        )}

        {/* ── Form Card ── */}
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div style={S.cardTitle}>
              <div style={S.iconCircle(tab.light, tab.color)}>{editId ? "✏️" : "📝"}</div>
              <div>
                <div>{editId ? "Edit Terms" : "Create New Terms"}</div>
                <div style={{ fontSize: 12, color: "#94A3B8", fontWeight: 400 }}>
                  {tab.label} · Version {terms.length + (editId ? 0 : 1)}
                </div>
              </div>
            </div>
            {editId && (
              <button style={S.cancelBtn} onClick={resetForm}>✕ Cancel</button>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Title */}
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>
                Title <span style={{ color: "#94A3B8", fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                style={S.input(tab.color)}
                placeholder="e.g., Terms of Service, Privacy Policy…"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {/* URL Field (only mode) */}
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>
                Terms URL <span style={{ color: "#EF4444" }}>*</span>
              </label>
              <input
                style={S.input(tab.color)}
                placeholder="https://example.com/terms"
                value={termsUrl}
                onChange={(e) => setTermsUrl(e.target.value)}
                type="url"
                required
              />
            </div>

            <hr style={S.divider} />

            <div style={{ display: "flex", gap: 12 }}>
              <button type="submit" style={S.submitBtn(tab.color)}>
                {editId ? "✏️ Update Terms" : "💾 Save Terms"}
              </button>
              {editId && (
                <button type="button" style={S.cancelBtn} onClick={resetForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* ── Search ── */}
        <div style={S.searchWrap}>
          <div style={S.searchBox(tab.color)}>
            <span>🔍</span>
            <input
              style={S.searchInput}
              placeholder={`Search ${tab.label.toLowerCase()} terms…`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8" }}
              >
                ✕
              </button>
            )}
          </div>
          <span style={S.statPill(tab.light, tab.color)}>
            {filtered.length} {filtered.length === 1 ? "item" : "items"}
          </span>
        </div>

        {/* ── Terms Grid ── */}
        <div style={S.card}>
          <div style={S.cardTitle}>
            <div style={S.iconCircle(tab.light, tab.color)}>📋</div>
            All Versions ({tab.label})
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "#94A3B8" }}>Loading…</div>
          ) : error ? (
            <div style={S.empty}>
              <div style={{ color: "#EF4444", marginBottom: 8 }}>⚠️ {error.message}</div>
              <button style={S.submitBtn(tab.color)} onClick={() => refetch()}>
                Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div style={S.empty}>
              <div style={S.emptyIcon(tab.light, tab.color)}>📄</div>
              <h4 style={{ margin: "0 0 6px", color: "#1E293B" }}>No terms found</h4>
              <p style={{ margin: 0, fontSize: 13 }}>
                {searchTerm
                  ? "Try a different search"
                  : `Create your first ${tab.label.toLowerCase()} terms`}
              </p>
            </div>
          ) : (
            <div style={S.grid}>
              {filtered.map((term) => (
                <div key={term._id} style={S.termCard(term.isActive, tab)}>
                  {/* Card header */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 10,
                    }}
                  >
                    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                      <span style={S.versionPill(tab.light, tab.color)}>v{term.version}</span>
                      {term.isActive && <span style={S.activePill(tab.color)}>ACTIVE</span>}
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "2px 7px",
                          borderRadius: 20,
                          background: term.termsUrl ? "#FEF3C7" : "#F0FDF4",
                          color: term.termsUrl ? "#92400E" : "#166534",
                        }}
                      >
                        {term.termsUrl ? "🔗 URL" : "📄 Text"}
                      </span>
                    </div>
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>{fmt(term.createdAt)}</span>
                  </div>

                  <div style={{ fontWeight: 700, fontSize: 14, color: "#0F172A", marginBottom: 6 }}>
                    {term.title || "Terms & Conditions"}
                  </div>

                  {/* Preview: show URL or a short preview of contentText (fallback) */}
                  {term.termsUrl ? (
                    <div>
                      <a
                        href={term.termsUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={S.urlChip(tab.color)}
                      >
                        🔗 {term.termsUrl.replace(/^https?:\/\/(www\.)?/, "").split("/")[0]}
                      </a>
                    </div>
                  ) : (
                    <p style={S.preview}>
                      {term.contentText?.length > 110
                        ? `${term.contentText.substring(0, 110)}…`
                        : term.contentText || "No URL or content provided"}
                    </p>
                  )}

                  {/* Card footer */}
                  <div style={S.cardFooter}>
                    <span style={{ fontSize: 11, color: "#94A3B8" }}>Updated {fmt(term.updatedAt)}</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      {!term.isActive && (
                        <button
                          style={S.actionBtn(tab.color, false)}
                          onClick={() => handleSetActive(term._id)}
                          title="Set Active"
                        >
                          ✓
                        </button>
                      )}
                      <button
                        style={S.actionBtn(tab.color, false)}
                        onClick={() => handleEdit(term)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        style={S.actionBtn(tab.color, true)}
                        onClick={() => handleDelete(term._id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}